import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import {
  Stack,
  Button,
  StatusExpandCard,
  useBreakpoints,
  Typography,
  Divider,
  useFinanceableTheme,
} from "@financeable-com-au/financeable-ui";
import { SUCCESS_MESSAGE, ERRORS_MESSAGE, getIsCommercialOrBusinessApplication, getIsAssetLoan } from "@/constants";
import {
  applicationSelector,
  getCurrentProductLenderId,
  userDetailsSelector,
  loanDetailsSelector,
  createProductQuote,
  removeProductQuote,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";
import {
  type ApplicationFormSlice,
  type ApplicationState,
  type UserDetailsState,
  type LoanDetails,
  RepaymentFrequency,
  Fee,
  ProductsQuote,
} from "@/store/slices/types/applicationFormSlice";
// @ts-expect-error: ignoring as it is existing js file
import { userPermissionsSelector } from "@/store/slices/userSlice";
import type { UserSlice, UserPermissionsDocument } from "@/store/slices/types/userSlice";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { useLenderCardContext } from "@/providers/LenderCardContextProvider";
import { useTemplateServices } from "@/hooks";
import InfoTooltip from "@/components/InfoTooltip";
// @ts-expect-error: ignoring as it is existing js file
import { getCustomerRole } from "@/components/Utils/helperFunction";
import { IDispatchResponse } from "@/utils/interface";
import { LenderCardInfo } from "./LenderCardInfo";
import { CriteriaExpanded } from "./CriteriaExpanded";
import { QuoteCalculatorExpanded } from "./QuoteCalculatorExpanded";
import { useLenderCardCriteria } from "../hooks";
import { DisclosureStatementModal } from "../DisclosureStatementModal";
import { UseLenderCardResult } from "../hooks/useLenderCard";
import { UseLenderCardCustomResult } from "../hooks/useLenderCardCustom";
import { ProductQuoteNoteModal } from "../ProductQuoteNoteModal";
import { LenderCardDownloadSubmitActions } from "../LenderCardDownloadSubmitActions/LenderCardDownloadSubmitActions";
import { LenderCardQuoteSubmitActions } from "../LenderCardQuoteSubmitActions/LenderCardQuoteSubmitActions";

const LenderCardDetailed = () => {
  const theme = useFinanceableTheme();
  const { canUseDisclosureServices } = useTemplateServices();
  const {
    product,
    isSingleLenderProduct,
    lenderCard,
    lenderCardCustom,
    isCustomQuote,
    inArrears,
    showCriteria,
    setShowCriteria,
    showQuoteCalculator,
    setShowQuoteCalculator,
    setShowDisclosureStatement,
  } = useLenderCardContext();
  const { smUp } = useBreakpoints();
  const { criteriaStatus } = useLenderCardCriteria();
  const permissions = useSelector<UserSlice, UserPermissionsDocument>(userPermissionsSelector);
  const application = useSelector<ApplicationFormSlice, ApplicationState>(applicationSelector);
  const loanDetails = useSelector<ApplicationFormSlice, LoanDetails>(loanDetailsSelector);
  const isCommercialOrBusinessApplication = getIsCommercialOrBusinessApplication(application.applicationType);
  const isAssetLoan = getIsAssetLoan(application.applicationType);
  // Use isQuoteOpen to determine if this is part of a quote flow.
  const { currentProductLenderId, isQuoteOpen, createProductQuoteLoader, removeProductQuoteLoader } = useSelector<
    ApplicationFormSlice,
    UserDetailsState
  >(userDetailsSelector);
  const isApplication = !isQuoteOpen;
  const dispatch = useDispatch();

  const [showProductQuoteNote, setShowProductQuoteNote] = useState<boolean>(false);

  useEffect(() => {
    if (isSingleLenderProduct) {
      setShowQuoteCalculator(false);
      setShowCriteria(true);
    }
  }, [isSingleLenderProduct]);

  const handleShowCriteria = () => {
    setShowQuoteCalculator(false);
    setShowCriteria(!showCriteria);
  };

  const handleShowQuoteCalculator = () => {
    setShowCriteria(false);
    setShowQuoteCalculator(!showQuoteCalculator);
  };

  const handle = {
    formatFeesForSaveQuote: (feesList: Fee[]) => {
      const updatedFeesList = feesList.map((fee) => {
        return {
          key: fee.key,
          name: fee.name,
          value: fee.value,
          frequency: fee.frequency,
          ...(fee.referrer !== undefined ? { referrer: fee.referrer } : {}),
        };
      });
      return updatedFeesList;
    },
    buildProductQuotePayload: (notes?: string) => {
      let lenderCardDetails: UseLenderCardCustomResult | UseLenderCardResult = lenderCard;
      if (isCustomQuote) {
        lenderCardDetails = lenderCardCustom;
      }

      const quotePayload = {
        repayments: lenderCardDetails?.repayments?.monthly?.value,
        repaymentFrequency:
          "repaymentFrequency" in lenderCardDetails
            ? (lenderCardDetails as UseLenderCardCustomResult)?.repaymentFrequency || RepaymentFrequency.Monthly
            : RepaymentFrequency.Monthly,
        loanAmount: lenderCardDetails?.loanAmount?.value,
        requestedLoanAmount: loanDetails?.requestedLoanAmount || 0,
        term: loanDetails?.term,
        balloon: loanDetails?.balloon,
        balloonAmount: loanDetails?.balloonAmount,
        deposit: loanDetails?.deposit,
        tradeIn: loanDetails?.tradeIn,
        originationFee: loanDetails?.originationFee,
        payout: loanDetails?.payout,
        fees: handle.formatFeesForSaveQuote(lenderCardDetails?.fees?.application),
        rate: lenderCardDetails?.rate?.default?.value || 0,
        rateAdjustment: loanDetails?.rateAdjustment,
        brokerage: lenderCardDetails?.brokerage?.percentage?.value || 0,
        brokerageAmount: lenderCardDetails?.brokerage?.amount?.value || 0,
        productName: product?.productName ?? "",
        productType: product?.productType ?? "",
        notes: notes || "",
        inArrears,
      };

      const productQuotePayload = {
        lender: product?.lender ?? "",
        type: application.applicationType,
        lenderId: product?.lenderId ?? "",
        applicationId: application._id,
        quote: quotePayload,
      };

      return productQuotePayload;
    },
    createProductQuote: async (productQuoteNotes: string) => {
      const productQuotePayload = handle.buildProductQuotePayload(productQuoteNotes);

      dispatch(createProductQuote(productQuotePayload)).then((res: IDispatchResponse) => {
        setShowProductQuoteNote(false);
        if (res?.error) {
          dispatch(
            openSnackbar({
              message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
        } else {
          dispatch(
            openSnackbar({
              message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.productQuoteCreated,
              intent: "success",
            }),
          );
        }
      });
    },
    removeProductQuote: async (productQuoteId: string) => {
      dispatch(getCurrentProductLenderId(product?.lender || ""));
      dispatch(removeProductQuote({ productQuoteId, applicationId: application._id })).then(
        (res: IDispatchResponse) => {
          if (res?.error) {
            dispatch(
              openSnackbar({
                message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
                intent: "danger",
              }),
            );
          } else {
            dispatch(
              openSnackbar({
                message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.productQuoteRemoved,
                intent: "success",
              }),
            );
          }
        },
      );
    },
  };

  const isDisableEdit = !permissions?.application?.edit || !application?.canEditApplication;

  const isSendDisclosureDisabled =
    !application.customers?.[0]?.emails?.find((email) => email.status === "current")?.address ||
    (isAssetLoan && !application.asset?.assetValue) ||
    isDisableEdit ||
    !canUseDisclosureServices;

  const isProductQuoteExists =
    application?.productsQuotes &&
    application?.productsQuotes.length > 0 &&
    application?.productsQuotes.find((pq) => pq.lender === product?.lender);

  const productsQuote = isProductQuoteExists
    ? (application?.productsQuotes?.find((pq) => pq.lender === product?.lender) as ProductsQuote)
    : null;

  const isMaximumQuotesSaved = application?.productsQuotes?.length >= 3;

  return (
    <>
      <Stack
        style={{
          ...(isProductQuoteExists &&
            productsQuote && {
              border: "2px solid",
              borderBottom: "3px solid",
              borderRadius: "8px",
              borderColor: theme.palette.primary[200],
            }),
        }}
      >
        <StatusExpandCard status={criteriaStatus}>
          <LenderCardInfo isCustomQuote={!!isCustomQuote} isProductQuoteExists={!!isProductQuoteExists} />
          <Stack
            marginTop={5}
            alignItems={smUp ? "center" : "flex-start"}
            flexDirection={{ xs: "column-reverse", sm: "row" }}
            rowGap={smUp ? 0 : 4}
            justifyContent="space-between"
          >
            <Stack flexDirection="row" flexWrap="wrap" columnGap={3} rowGap={1}>
              <Button
                onClick={handleShowCriteria}
                variant="inline"
                endDecorator={
                  <ExpandMoreIcon
                    sx={{
                      fontSize: "1rem",
                      transition: "transform 0.2s ease",
                      ...(showCriteria && { transform: "rotate(180deg)" }),
                    }}
                  />
                }
              >
                Show Details
              </Button>
              <Button
                onClick={handleShowQuoteCalculator}
                variant="inline"
                endDecorator={
                  <ExpandMoreIcon
                    sx={{
                      fontSize: "1rem",
                      transition: "transform 0.2s ease",
                      ...(showQuoteCalculator && { transform: "rotate(180deg)" }),
                    }}
                  />
                }
              >
                Customise Quote
              </Button>
            </Stack>

            <Stack flexDirection="row" flexWrap="wrap" columnGap={3} rowGap={1}>
              <Stack flexDirection="row" alignItems="center" columnGap={1}>
                {!isProductQuoteExists && (
                  <Button
                    variant="secondary"
                    intent="neutral"
                    size="sm"
                    disabled={isMaximumQuotesSaved || isDisableEdit}
                    endDecorator={<SaveOutlinedIcon sx={{ fontSize: "1rem" }} />}
                    onClick={() => setShowProductQuoteNote(true)}
                  >
                    {isApplication ? "Save Quote" : "Select Quote"}
                  </Button>
                )}
                {isProductQuoteExists && productsQuote && (
                  <Button
                    variant="secondary"
                    intent="neutral"
                    size="sm"
                    endDecorator={<SaveOutlinedIcon sx={{ fontSize: "1rem" }} />}
                    onClick={() => handle.removeProductQuote(productsQuote?._id)}
                    disabled={(removeProductQuoteLoader && currentProductLenderId === product?.lender) || isDisableEdit}
                  >
                    {removeProductQuoteLoader && currentProductLenderId === product?.lender
                      ? "Removing..."
                      : "Remove Quote"}
                  </Button>
                )}
                {isMaximumQuotesSaved && !isProductQuoteExists && (
                  <InfoTooltip
                    title={
                      <Stack padding={2} flexDirection="column" rowGap={2}>
                        <Typography level="body-sm" bold>
                          You can save up to 3 quotes.
                        </Typography>
                        <Typography mb={2}>You can remove a quote to save a new one.</Typography>
                      </Stack>
                    }
                  />
                )}
              </Stack>
              {isApplication && !isCommercialOrBusinessApplication && (
                <Stack flexDirection="row" alignItems="center">
                  {isSendDisclosureDisabled && (
                    <InfoTooltip
                      title={
                        <Stack padding={2} flexDirection="column" rowGap={2}>
                          <Typography level="body-sm" bold>
                            Sending disclosure requires the following
                          </Typography>
                          <Divider />
                          <Stack flexDirection="column" rowGap={1}>
                            {!canUseDisclosureServices ? (
                              <Typography>Add disclosure document template</Typography>
                            ) : (
                              <>
                                <Typography>
                                  {getCustomerRole(
                                    application.applicationType,
                                    application.entities,
                                    application.purpose,
                                  )}{" "}
                                  email
                                </Typography>
                                {isAssetLoan && !application.asset?.assetValue && <Typography>Asset value</Typography>}
                              </>
                            )}
                          </Stack>
                        </Stack>
                      }
                    />
                  )}
                  <Button
                    variant="secondary"
                    intent="neutral"
                    size="sm"
                    disabled={isSendDisclosureDisabled}
                    endDecorator={<SendOutlinedIcon sx={{ fontSize: "1rem" }} />}
                    onClick={() => {
                      setShowDisclosureStatement(true);
                    }}
                  >
                    Send Disclosure
                  </Button>
                </Stack>
              )}
              <LenderCardDownloadSubmitActions />
              <LenderCardQuoteSubmitActions />
            </Stack>
          </Stack>
          <CriteriaExpanded />
          <QuoteCalculatorExpanded productsQuote={productsQuote ?? null} />
        </StatusExpandCard>
      </Stack>

      <DisclosureStatementModal isCustomQuote={!!isCustomQuote} />

      {showProductQuoteNote && (
        <ProductQuoteNoteModal
          open={showProductQuoteNote}
          setOpen={setShowProductQuoteNote}
          handleSaveProductQuote={(productQuoteNotes: string) => handle.createProductQuote(productQuoteNotes)}
          isSaving={createProductQuoteLoader}
          mode="save"
        />
      )}
    </>
  );
};

export { LenderCardDetailed };
